import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, API, STATUS_ORDER, orderMatchesSearch } from "@/lib/api";
import { useCanAdmin } from "@/lib/useCanAdmin";
import { X, MagnifyingGlass, Warning, CaretLeft, CaretRight, Stack, FunnelSimple, FileXls, Archive } from "@phosphor-icons/react";
import { toast } from "sonner";
import HintButton from "@/components/HintButton";
import ScannerBar from "@/components/ScannerBar";
import KanbanFilterModal, { EMPTY_FILTERS } from "@/components/kanban/KanbanFilterModal";
import PinModal from "@/components/kanban/PinModal";
import BulkMoveModal from "@/components/kanban/BulkMoveModal";
import SelectionActionBar from "@/components/kanban/SelectionActionBar";
import KanbanActiveFilters from "@/components/kanban/KanbanActiveFilters";
import KanbanExportModal from "@/components/kanban/KanbanExportModal";
import KanbanColumn from "@/components/kanban/KanbanColumn";
import { overdueDays, urgencySort, matchesAdvanced, countActiveFilters } from "@/components/kanban/kanbanUtils";
import { useRealtimeOrders } from "@/lib/useRealtimeOrders";

export default function KanbanBoard() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dragging, setDragging] = useState(null);
  const [pending, setPending] = useState(null); // {order, toStatus}
  const [showBulk, setShowBulk] = useState(false);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [selectionConfirm, setSelectionConfirm] = useState(false);
  const [search, setSearch] = useState("");
  const [visible, setVisible] = useState({}); // {status: count} — 100 iniciais, +50 por clique
  const [scanValue, setScanValue] = useState("");
  const [highlightId, setHighlightId] = useState(null);
  const [advFilters, setAdvFilters] = useState(EMPTY_FILTERS);
  const [showFilter, setShowFilter] = useState(false);
  const [counts, setCounts] = useState(null); // contagens exatas por status (cache do servidor)
  const [exporting, setExporting] = useState(null); // "all" | status da coluna | null
  const [showExport, setShowExport] = useState(false);
  const [exportCols, setExportCols] = useState(() => new Set(STATUS_ORDER));
  const { canAdmin } = useCanAdmin();

  const exportBoard = async (status) => {
    setExporting(status);
    try {
      const qs = status && status !== "all" ? `?status=${encodeURIComponent(status)}` : "";
      const resp = await fetch(`${API}/orders/export/board${qs}`, { credentials: "include" });
      if (!resp.ok) throw new Error();
      const blob = await resp.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      let suffix = status;
      if (status === "all") suffix = "quadro";
      else if (status.includes(",")) suffix = "colunas";
      a.download = `kanban_${suffix}_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success("Exportação para Excel concluída");
    } catch {
      toast.error("Falha ao exportar para Excel");
    } finally {
      setExporting(null);
    }
  };

  const submitExport = async () => {
    if (exportCols.size === 0) {
      toast.error("Marque pelo menos uma coluna");
      return;
    }
    setShowExport(false);
    const status = exportCols.size === STATUS_ORDER.length
      ? "all"
      : STATUS_ORDER.filter((s) => exportCols.has(s)).join(",");
    await exportBoard(status);
  };

  const applyBoard = (data) => {
    setOrders(data?.items || (Array.isArray(data) ? data : []));
    if (data?.counts) setCounts(data.counts);
  };

  const load = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      if (!silent) {
        // Estágio 1: os 50 mais urgentes de cada coluna — render imediato
        const { data } = await api.get("/orders/board", { params: { per_status: 50 } });
        applyBoard(data);
        setLoading(false);
      }
      // Estágio 2 (ou reload silencioso): board completo em background
      const { data } = await api.get("/orders/board");
      applyBoard(data);
    } catch (e) {
      console.warn("KanbanBoard orders load:", e?.response?.status || e?.message);
      if (!silent) setOrders([]);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // Fallback: polling lento caso o WebSocket caia
    const i = setInterval(() => load(true), 30000);
    return () => clearInterval(i);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Tempo real: recarrega quando qualquer estação movimenta um pedido
  useRealtimeOrders(() => load(true));

  // Busca manual: se nada bate localmente (carga parcial de 50/coluna),
  // consulta o servidor com debounce e mescla — o pedido aparece na coluna dele.
  useEffect(() => {
    const q = search.trim();
    if (!q) return undefined;
    const t = setTimeout(async () => {
      if (orders.some((o) => orderMatchesSearch(o, q))) return;
      try {
        const { data } = await api.get("/orders", { params: { search: q, limit: 20 } });
        const items = data?.items || [];
        if (items.length) {
          setOrders((prev) => {
            const ids = new Set(prev.map((p) => p.id));
            const add = items.filter((i) => !ids.has(i.id));
            return add.length ? [...prev, ...add] : prev;
          });
        }
      } catch (e) {
        console.warn("kanban search fallback:", e?.message);
      }
    }, 400);
    return () => clearTimeout(t);
  }, [search, orders]);

  // Manipulação do scanner de código de barras: leitores atuam como teclado —
  // basta enviar o número + Enter no input dedicado. Rolamos até o card e destacamos.
  // Também usada pelo auto-focus vindo de /kanban?focus=NUM (click em push notification).
  const focusOrderByNumber = async (raw) => {
    if (!raw) return false;
    const num = String(raw).trim().replace(/^#/, "");
    // Varredura completa: nº do pedido, contato, telefone ou pedido na loja
    let found = orders.find((o) => String(o.bling_number || "") === num) ||
      orders.find((o) => orderMatchesSearch(o, num));
    let list = orders;
    if (!found) {
      // Pode ainda não estar no board (carga parcial de 50/coluna) — busca no servidor
      try {
        const { data } = await api.get("/orders", { params: { search: num, limit: 5 } });
        const items = data?.items || [];
        const hit = items.find((o) => String(o.bling_number || "") === num) || items[0];
        if (hit) {
          found = hit;
          if (!list.some((p) => p.id === hit.id)) {
            list = [...list, hit];
            setOrders(list);
          }
        }
      } catch (e) {
        console.warn("focusOrderByNumber server search:", e?.message);
      }
    }
    if (!found) {
      toast.error(`Nenhum pedido encontrado para "${num}".`, { duration: 3000 });
      setScanValue("");
      return false;
    }
    // Limpa filtros para garantir que o card fique visível
    setSearch("");
    setAdvFilters(EMPTY_FILTERS);
    // Garante que o card do pedido está paginado dentro da coluna
    setVisible((prev) => {
      const cur = prev[found.production_status] || 100;
      const sameCol = list
        .filter((o) => o.production_status === found.production_status)
        .sort(urgencySort);
      const idx = sameCol.findIndex((o) => o.id === found.id);
      const target = Math.max(cur, idx + 20);
      if (target === cur) return prev;
      return { ...prev, [found.production_status]: target };
    });
    setHighlightId(found.id);
    setScanValue("");
    // Rolagem em dois passos: primeiro horizontal até a coluna, depois vertical
    // até o card. Aguarda o re-render após limpar o search e expandir paginação.
    setTimeout(() => {
      const wrap = document.querySelector('.kanban-scroll-wrap');
      const col = document.querySelector(`[data-testid="kanban-col-${found.production_status}"]`);
      if (wrap && col) {
        const wrapRect = wrap.getBoundingClientRect();
        const colRect = col.getBoundingClientRect();
        const offset = wrap.scrollLeft + (colRect.left - wrapRect.left) - (wrapRect.width - colRect.width) / 2;
        wrap.scrollTo({ left: Math.max(0, offset), behavior: "smooth" });
      }
      const el = document.querySelector(`[data-testid="kanban-card-${found.bling_number}"]`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" });
    }, 300);
    setTimeout(() => setHighlightId(null), 4000);
    toast.success(`Pedido #${found.bling_number} · ${found.customer}`, { duration: 3000 });
    return true;
  };

  const handleScan = () => focusOrderByNumber(scanValue);

  // Garantia pós-scan: quando os dados completos chegam durante o destaque,
  // re-expande a paginação da coluna até a posição real do pedido e re-rola.
  useEffect(() => {
    if (!highlightId) return;
    const target = orders.find((o) => o.id === highlightId);
    if (!target) return;
    const sameCol = orders
      .filter((o) => o.production_status === target.production_status)
      .sort(urgencySort);
    const idx = sameCol.findIndex((o) => o.id === highlightId);
    if (idx >= 0) {
      setVisible((prev) => {
        const cur = prev[target.production_status] || 100;
        return idx >= cur ? { ...prev, [target.production_status]: idx + 20 } : prev;
      });
    }
    const t = setTimeout(() => {
      const el = document.querySelector(`[data-testid="kanban-card-${target.bling_number}"]`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" });
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [highlightId, orders]);

  // Auto-foco: quando abre com /kanban?focus=NUM (ex: click em notificação push),
  // aguarda pedidos carregarem e chama focusOrderByNumber (mesmo fluxo do scanner).
  useEffect(() => {
    if (loading || orders.length === 0) return;
    const params = new URLSearchParams(window.location.search);
    const focus = params.get("focus");
    if (!focus) return;
    // Roda 1x — remove o param da URL pra não repetir em re-renders
    const url = new URL(window.location.href);
    url.searchParams.delete("focus");
    window.history.replaceState({}, "", url.toString());
    // Aguarda 1 tick pra garantir que o render inicial já foi commitado
    setTimeout(() => focusOrderByNumber(focus), 50);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, orders]);

  const matchesSearch = (o) => orderMatchesSearch(o, search);

  const matchesAll = (o) => matchesSearch(o) && matchesAdvanced(o, advFilters);
  const activeFilterCount = countActiveFilters(advFilters);

  const byStatus = (s) => orders.filter(o => o.production_status === s && matchesAll(o)).sort(urgencySort);
  const visibleCount = (s) => visible[s] || 100;
  const showMore = (s) => setVisible((prev) => ({ ...prev, [s]: visibleCount(s) + 50 }));
  const totalOverdue = orders.filter((o) => overdueDays(o) > 0 && matchesAll(o)).length;

  const onDragStart = (o) => setDragging(o);
  const onDragOver = (e) => e.preventDefault();
  const onDrop = (toStatus) => {
    if (!dragging) return;
    const from = dragging.production_status;
    if (from === toStatus) { setDragging(null); return; }
    // O modal permite escolher o posto destino; pular postos é validado pelo
    // backend via permissão "kanban_skip_stages".
    setPending({ order: dragging, toStatus });
    setDragging(null);
  };

  const startArrowMove = (order, direction) => {
    const curIdx = STATUS_ORDER.indexOf(order.production_status);
    if (curIdx < 0) return;
    const nextIdx = curIdx + direction;
    if (nextIdx < 0 || nextIdx >= STATUS_ORDER.length) return;
    setPending({ order, toStatus: STATUS_ORDER[nextIdx] });
  };

  const onCardClick = (o) => {
    if (selectionMode) {
      const next = new Set(selectedIds);
      if (next.has(o.id)) next.delete(o.id); else next.add(o.id);
      setSelectedIds(next);
    } else {
      navigate(`/orders/${o.id}`);
    }
  };

  const onCardToggle = (o, checked) => {
    const next = new Set(selectedIds);
    if (checked) next.add(o.id); else next.delete(o.id);
    setSelectedIds(next);
  };

  // Seleciona TODOS os cards da coluna (inclusive os não exibidos pela paginação)
  const onToggleColumn = (full) => {
    const idsHere = full.map((o) => o.id);
    const allSelected = idsHere.every((id) => selectedIds.has(id));
    const next = new Set(selectedIds);
    if (allSelected) idsHere.forEach((id) => next.delete(id));
    else idsHere.forEach((id) => next.add(id));
    setSelectedIds(next);
  };

  const confirmMove = async (code, password, toStatus) => {
    const dest = toStatus || pending.toStatus;
    try {
      await api.post(`/orders/${pending.order.id}/move`, {
        to_status: dest,
        collaborator_code: code,
        collaborator_password: password,
        expected_from_status: pending.order.production_status,
      });
    } catch (err) {
      // Conflito: outra estação já moveu este pedido — atualiza a tela
      if (err?.response?.status === 409) {
        toast.error(err.response.data?.detail || "Pedido já movido por outra estação.", { duration: 6000 });
        setPending(null);
        await load(true);
        return;
      }
      throw err;
    }
    // Atualização otimista: move o card na hora, sem esperar o reload completo
    const movedId = pending.order.id;
    setPending(null);
    setOrders((prev) => prev.map((x) => x.id === movedId
      ? { ...x, production_status: dest, last_moved_at: new Date().toISOString() }
      : x));
    // Após cada apontamento: volta ao topo e limpa a busca para o próximo colaborador
    setSearch("");
    window.scrollTo({ top: 0, behavior: "smooth" });
    // Reconciliação em background (não bloqueia a UI)
    load(true);
  };

  const scrollWrapBy = (left) => {
    const wrap = document.querySelector('.kanban-scroll-wrap');
    if (!wrap) return;
    if (left === "end") wrap.scrollTo({ left: wrap.scrollWidth, behavior: "smooth" });
    else wrap.scrollBy({ left, behavior: "smooth" });
  };

  const isFiltered = Boolean(search) || activeFilterCount > 0;

  return (
    <div className="px-4 md:px-6 py-4 flex flex-col h-[calc(100vh-0px)] min-h-0">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
        <div className="flex items-baseline gap-3 flex-wrap">
          <div className="overline text-zinc-500">FLUXO DE PRODUÇÃO</div>
          <h1 className="font-head text-xl font-black tracking-tight leading-none">Kanban</h1>
          <HintButton text="Arraste os cartões ou use as setas ← / → em cada cartão. No modal de confirmação você escolhe o posto destino. Cada movimentação exige código + senha do colaborador. Avançar pulando postos exige a permissão 'Avançar pulando postos' (colaborador ou estação)." />
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative w-full md:w-72">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <input
              className="input pl-9 !py-2 text-xs"
              placeholder="Buscar nº pedido, cliente, contato ou pedido na loja"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="kanban-search"
            />
            {search && (
              <button onClick={() => setSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-black"><X size={14} /></button>
            )}
          </div>
          {!loading && totalOverdue > 0 && (
            <div className="flex items-center gap-1.5 border border-[#E60000] bg-red-50 px-2 py-1" data-testid="kanban-overdue-banner" title={`${totalOverdue} pedido(s) atrasado(s)`}>
              <Warning size={14} weight="fill" color="#E60000" />
              <span className="font-mono font-bold text-[#E60000] text-sm leading-none">{totalOverdue}</span>
              <span className="text-[9px] font-bold uppercase tracking-widest text-[#E60000]">atrasado{totalOverdue > 1 ? "s" : ""}</span>
            </div>
          )}
          <button
            type="button"
            onClick={() => setShowFilter(true)}
            className={`!py-1.5 !px-3 text-xs flex items-center gap-1.5 border border-black transition-colors ${activeFilterCount > 0 ? "bg-black text-white" : "bg-white text-black hover:bg-zinc-100"}`}
            data-testid="kanban-filter-btn"
            title="Filtro avançado: cliente, cores, SKU, atrasados, tempo parado"
          >
            <FunnelSimple size={14} weight="bold" /> Filtros{activeFilterCount > 0 ? ` (${activeFilterCount})` : ""}
          </button>
          <button
            type="button"
            onClick={() => setShowExport(true)}
            disabled={!!exporting}
            className="!py-1.5 !px-3 text-xs flex items-center gap-1.5 border border-black bg-white text-black hover:bg-zinc-100 transition-colors disabled:opacity-50"
            data-testid="kanban-export-all-btn"
            title="Exportar para Excel — escolha quais colunas do quadro incluir"
          >
            <FileXls size={14} weight="bold" /> {exporting ? "Exportando..." : "Excel"}
          </button>
          <button
            type="button"
            onClick={() => navigate("/arquivados")}
            className="!py-1.5 !px-3 text-xs flex items-center gap-1.5 border border-black bg-white text-black hover:bg-zinc-100 transition-colors"
            data-testid="kanban-archived-btn"
            title="Pedidos enviados há mais de 30 dias, arquivados automaticamente"
          >
            <Archive size={14} weight="bold" /> Arquivados
          </button>
          {canAdmin && (
            <button
              type="button"
              onClick={() => setShowBulk(true)}
              className="btn !py-1.5 !px-3 text-xs flex items-center gap-1.5"
              data-testid="kanban-bulk-move-btn"
              title="Mover pedidos em lote por intervalo"
            >
              <Stack size={14} weight="bold" /> Mover em lote
            </button>
          )}
          {canAdmin && (
            <button
              type="button"
              onClick={() => {
                if (selectionMode) { setSelectedIds(new Set()); }
                setSelectionMode((v) => !v);
              }}
              className={`!py-1.5 !px-3 text-xs flex items-center gap-2 border border-black transition-colors ${selectionMode ? "bg-black text-white" : "bg-white text-black hover:bg-zinc-100"}`}
              data-testid="kanban-selection-toggle"
              title="Ativa seleção múltipla com checkboxes"
            >
              <span className={`inline-block w-8 h-4 relative ${selectionMode ? "bg-white" : "bg-zinc-300"}`}>
                <span className={`absolute top-0 w-4 h-4 transition-all ${selectionMode ? "left-4 bg-black" : "left-0 bg-white border border-zinc-400"}`} />
              </span>
              Selecionar
            </button>
          )}
        </div>
      </div>

      {/* Scanner de código de barras — barra fixa flutuante quando a página rola */}
      <ScannerBar
        value={scanValue}
        onChange={setScanValue}
        onScan={handleScan}
        onCameraResult={(code) => { setScanValue(code); focusOrderByNumber(code); }}
        testidPrefix="kanban"
        className="mb-3"
      />

      {activeFilterCount > 0 && (
        <KanbanActiveFilters advFilters={advFilters} setAdvFilters={setAdvFilters} />
      )}

      {loading ? (
        <div className="text-zinc-400">Carregando…</div>
      ) : (
        <div className="relative flex-1 min-h-0">
          {/* Botões flutuantes de navegação rápida entre colunas */}
          <button
            type="button"
            onClick={() => scrollWrapBy(-260)}
            className="hidden md:flex items-center justify-center absolute left-0 top-1/2 -translate-y-1/2 z-20 bg-black text-white w-9 h-14 shadow-lg hover:bg-zinc-800"
            title="Coluna anterior"
            data-testid="kanban-nav-prev"
          >
            <CaretLeft size={22} weight="bold" />
          </button>
          <button
            type="button"
            onClick={() => scrollWrapBy(260)}
            className="hidden md:flex items-center justify-center absolute right-0 top-1/2 -translate-y-1/2 z-20 bg-black text-white w-9 h-14 shadow-lg hover:bg-zinc-800"
            title="Próxima coluna"
            data-testid="kanban-nav-next"
          >
            <CaretRight size={22} weight="bold" />
          </button>
          <button
            type="button"
            onClick={() => scrollWrapBy("end")}
            className="hidden md:flex items-center justify-center absolute right-9 top-1/2 -translate-y-1/2 z-20 bg-zinc-800 text-white h-14 px-2 shadow-lg hover:bg-black text-[10px] font-bold uppercase tracking-widest"
            title="Ir para a última coluna"
            data-testid="kanban-nav-end"
          >
            FIM ›
          </button>
          <div className="kanban-scroll-wrap">
            <div className="flex gap-3 h-full min-w-max" data-testid="kanban-board">
              {STATUS_ORDER.map((s, colIdx) => {
                const full = byStatus(s);
                // Sem filtros ativos, usa a contagem exata do servidor (cacheada);
                // com filtro/busca, conta o que está visível no cliente.
                const totalInCol = !isFiltered && counts ? (counts[s] ?? full.length) : full.length;
                return (
                  <KanbanColumn
                    key={s}
                    status={s}
                    colIdx={colIdx}
                    lastIdx={STATUS_ORDER.length - 1}
                    full={full}
                    visibleCount={visibleCount(s)}
                    totalInCol={totalInCol}
                    selectionMode={selectionMode}
                    canAdmin={canAdmin}
                    selectedIds={selectedIds}
                    exporting={exporting}
                    draggingId={dragging?.id}
                    highlightId={highlightId}
                    onDragOver={onDragOver}
                    onDrop={onDrop}
                    onToggleColumn={onToggleColumn}
                    onExportCol={exportBoard}
                    onShowMore={showMore}
                    onCardClick={onCardClick}
                    onCardToggle={onCardToggle}
                    onCardDragStart={onDragStart}
                    onCardDragEnd={() => setDragging(null)}
                    onArrowMove={startArrowMove}
                  />
                );
              })}
            </div>
          </div>
        </div>
      )}

      {pending && (
        <PinModal
          orderNumber={pending.order.bling_number}
          fromStatus={pending.order.production_status}
          initialToStatus={pending.toStatus}
          onConfirm={confirmMove}
          onCancel={() => setPending(null)}
        />
      )}

      {showFilter && (
        <KanbanFilterModal
          orders={orders}
          filters={advFilters}
          onApply={(f) => { setAdvFilters(f); setShowFilter(false); }}
          onClose={() => setShowFilter(false)}
        />
      )}

      {showBulk && (
        <BulkMoveModal
          onClose={() => setShowBulk(false)}
          onDone={() => load()}
        />
      )}

      {showExport && (
        <KanbanExportModal
          counts={counts}
          exportCols={exportCols}
          setExportCols={setExportCols}
          onSubmit={submitExport}
          onClose={() => setShowExport(false)}
        />
      )}

      {selectionMode && selectedIds.size > 0 && (
        <SelectionActionBar
          count={selectedIds.size}
          onClear={() => setSelectedIds(new Set())}
          onMove={() => setSelectionConfirm(true)}
        />
      )}

      {selectionConfirm && (
        <BulkMoveModal
          initialIds={Array.from(selectedIds)}
          onClose={() => setSelectionConfirm(false)}
          onDone={() => {
            setSelectedIds(new Set());
            setSelectionConfirm(false);
            setSelectionMode(false);
            load();
          }}
        />
      )}
    </div>
  );
}
